import React from 'react';
import { Button, Form } from 'react-bootstrap';
import { FaEye, FaCheckCircle, FaArrowUp, FaArrowDown, FaStar, FaRegStar } from 'react-icons/fa';
import CategorySelect from './CategorySelect';
import { formatGainPct, gainClass } from '../utils/gainFormat';

const STAR_ICON_SIZE = '1.1rem';

// Renders one Watchlist row exactly as it appears on the Watchlist page.
// Shared by Watchlist.js (the source page) and TickerSearch.js so both
// places stay in sync. `extraRowStyle` lets the caller apply the seasonal
// "owned holding" background tint Watchlist.js computes from ThemeContext;
// it's optional (defaults to none) since TickerSearch.js has no reason to
// replicate that theming.
const WatchlistRow = ({
  item,
  index,
  selected,
  highlighted,
  removing,
  deleting,
  globalPriceUpdateLoading,
  priorityLoading,
  extraRowStyle,
  onRowClick,
  onPriorityChange,
  onPriceUpdateToggle,
  onCategoryUpdate,
  onPromote,
  onDemote,
}) => {
  const currentStars = item.priority_stars || 0;

  return (
    <tr
      className={`watchlist-row${selected ? ' watchlist-row-selected' : ''}${highlighted ? ' watchlist-row-highlight' : ''}${removing ? ' watchlist-row-removing' : ''}${item.is_purchased ? ' watchlist-owned-row' : ''}`}
      onClick={() => onRowClick(item.id)}
      style={{ cursor: 'pointer', ...extraRowStyle }}
    >
      <td className="text-center" style={{ width: '40px', fontSize: '0.85rem', color: '#888' }}>{index + 1}</td>
      <td className="text-center" style={{ width: '104px' }} onClick={e => e.stopPropagation()}>
        <div className="d-inline-flex align-items-center justify-content-center" style={{ gap: '2px' }}>
          {[1, 2, 3].map((starValue) => {
            const filled = starValue <= currentStars;
            return (
              <button
                key={starValue}
                type="button"
                className="btn btn-link p-0"
                style={{ textDecoration: 'none', lineHeight: 1, opacity: priorityLoading ? 0.5 : 1 }}
                onClick={() => onPriorityChange(item, currentStars === starValue ? 0 : starValue)}
                disabled={priorityLoading}
                title={`Set leader rating to ${starValue} star${starValue > 1 ? 's' : ''}`}
                aria-label={`Set leader rating to ${starValue} star${starValue > 1 ? 's' : ''}`}
              >
                {filled ? (
                  <FaStar style={{ color: '#f5c518', fontSize: STAR_ICON_SIZE }} />
                ) : (
                  <FaRegStar style={{ color: '#b0b0b0', fontSize: STAR_ICON_SIZE }} />
                )}
              </button>
            );
          })}
        </div>
      </td>
      <td style={{ width: '60px' }}>{item.ticker}</td>
      <td className="text-center" style={{ width: '80px', fontSize: '0.75rem' }}>
        {item.latest_note_updated_at ? (() => {
          const noteDate = new Date(item.latest_note_updated_at);
          const today = new Date();
          const isToday = noteDate.toDateString() === today.toDateString();
          const timeStr = noteDate.toLocaleDateString('en-US', {
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: false
          }).replace(',', '');

          return isToday ? (
            <span className="badge bg-success" style={{ fontSize: '0.7rem', fontWeight: 'normal' }}>
              {timeStr}
            </span>
          ) : timeStr;
        })() : '-'}
      </td>
      <td className={`text-end ${gainClass(item.gain, 'watchlist')}`} style={{ width: '60px' }}>{formatGainPct(item.gain_pct)}</td>
      <td className="text-center" style={{ width: '60px' }}>
        {item.is_purchased ? (
          <FaCheckCircle color="#28a745" size="1.2em" title="Owned" />
        ) : (
          <FaEye color="#007bff" size="1.2em" title="Watching" />
        )}
      </td>
      <td className="text-center" style={{ width: '90px' }} onClick={e => e.stopPropagation()}>
        <Form.Check
          type="switch"
          id={`watchlist-price-toggle-${item.id}`}
          checked={item.is_purchased ? true : item.include_in_price_updates !== false}
          disabled={item.is_purchased || globalPriceUpdateLoading}
          onChange={(e) => {
            e.stopPropagation();
            onPriceUpdateToggle(item, e.target.checked);
          }}
          title={
            item.is_purchased
              ? 'Active holdings always receive price updates.'
              : 'Toggle price updates for this ticker.'
          }
        />
      </td>
      <td className="text-end" style={{ width: '172px' }}>
        <CategorySelect
          tickerId={item.id}
          currentCategory={item.category ? { category_id: item.category_id, name: item.category } : null}
          onCategoryUpdate={onCategoryUpdate}
          isWatchlist={true}
        />
      </td>
      <td className="text-end align-middle" style={{ width: '180px' }} onClick={e => e.stopPropagation()}>
        <Button
          variant="outline-success"
          size="sm"
          className="btn-sm me-1"
          style={{ padding: '2px 6px', fontSize: '0.75rem', border: '1px solid #28a745', backgroundColor: 'transparent' }}
          onClick={() => onPromote(item.id)}
          disabled={deleting}
          title="Promote to Readylist"
        >
          {deleting ? (
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
          ) : (
            <FaArrowUp size="0.8em" />
          )}
        </Button>
        <Button
          variant="outline-info"
          size="sm"
          className="btn-sm me-1"
          style={{ padding: '2px 6px', fontSize: '0.75rem', border: '1px solid #17a2b8', backgroundColor: 'transparent' }}
          onClick={() => onDemote(item.id)}
          disabled={deleting}
          title="Demote to Basing"
        >
          {deleting ? (
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
          ) : (
            <FaArrowDown size="0.8em" />
          )}
        </Button>
      </td>
    </tr>
  );
};

WatchlistRow.defaultProps = {
  extraRowStyle: {},
};

export default WatchlistRow;
